import { useEffect, useState } from 'react';
import { listarEquipe, podeFazer } from '../../acesso';
import type { Eu, MembroDaEquipe } from '../../acesso';
import { EstadoVazio, ErroDeArea, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import { agrupar } from '../../design/graficos/agrupar';
import { MODELOS_TEMPO, fmt, serieNoTempo } from '../../design/graficos/modelos';
import {
  diaLongo,
  hojeBrasilia,
  horaCurta,
  inteiro,
  lerGaleria,
  tomDaVariacao,
} from '../../paineis';
import type {
  GaleriaDeGraficos,
  Indicador,
  ItemDeAtencao,
  ModuloDaOperacao,
  PainelDoDia,
  Periodo,
} from '../../paineis';
import { GraficoPrincipal } from './DestaquesDoDashboard';
import {
  capitalizar,
  comMilhar,
  dadosDo,
  dinheiro,
  fonteDo,
  iniciais,
  minutosAtras,
  primeiroNome,
  saudacao,
  useAgora,
  usePainelDoDia,
} from './home-comum';
import './home.css';
import {
  BarraDeFiltros,
  Bloco,
  Carregando,
  Kpi,
  diaCurto,
  lerFiltros,
  nomeCurto,
  pctVar,
  soma,
  textoDoPeriodo,
  variacao,
} from './pecasDaVisao';

/**
 * VISÃO GERAL — "como está a loja, o que precisa de atenção, onde clicar", agora com FILTROS:
 * o período (hoje, ontem, semana de sábado a sexta, mês, trimestre, ano ou um período livre) e o
 * balconista. Os números vêm do HOS (API) e dos lançamentos; cada comparação diz contra o quê:
 * hoje compara com a média dos 4 mesmos dias da semana ATÉ A MESMA HORA; um período compara com
 * o período anterior de mesmo tamanho. O que é do momento (atenção, operação, atividade) é sempre
 * de agora. Os filtros ficam no endereço: dá para mandar o link e abrir igual.
 */

const LOJA = 'Rede Inova · Muriaé';
const RELEITURA_MS = 120_000;

/** A galeria do período (e da pessoa), relida quando o filtro muda. */
function useGaleria(p: Periodo, pessoa: string | null, versao: number) {
  const [g, setG] = useState<{ chave: string; dados: GaleriaDeGraficos | null } | null>(null);
  const chave = `${p.de}|${p.ate}|${pessoa ?? ''}`;
  useEffect(() => {
    let vivo = true;
    void lerGaleria(p, pessoa).then((r) => {
      if (vivo) setG({ chave, dados: r.ok ? r.dados : null });
    });
    return () => {
      vivo = false;
    };
    // a chave resume o período e a pessoa; `versao` força reler ("Atualizar")
  }, [chave, versao]);
  return g && g.chave === chave
    ? { carregando: false, dados: g.dados }
    : { carregando: true, dados: g?.dados ?? null };
}

function useEquipe() {
  const [pessoas, setPessoas] = useState<readonly MembroDaEquipe[]>([]);
  useEffect(() => {
    let vivo = true;
    void listarEquipe().then((r) => {
      if (vivo && r.ok) setPessoas(r.dados.pessoas.filter((x) => x.accountId));
    });
    return () => {
      vivo = false;
    };
  }, []);
  return pessoas;
}

export function Home({ eu, parametros }: { eu: Eu | null; parametros: URLSearchParams }) {
  const hoje = hojeBrasilia();
  const f = lerFiltros(parametros, hoje);
  const umDia = f.periodo.de === f.periodo.ate;
  const { estado, carregar } = usePainelDoDia(umDia ? f.periodo.de : undefined, RELEITURA_MS);
  const [versao, setVersao] = useState(0);
  const galeria = useGaleria(f.periodo, f.pessoa, versao);
  const equipe = useEquipe();
  const agora = useAgora();

  if (estado.fase === 'erro' && estado.falha.tipo === 'sem-acesso')
    return <SemAcesso area="A visão geral da operação" />;

  const ultimo = dadosDo(estado);
  const d = ultimo?.dados ?? null;
  const offline = estado.fase === 'erro' && estado.falha.tipo === 'offline';
  const quando = textoDoPeriodo(f.filtro, f.periodo, hoje);
  const atualizar = () => {
    void carregar();
    setVersao((v) => v + 1);
  };

  return (
    <section className="vg">
      {estado.fase === 'erro' && ultimo && (
        <div className="hm-faixa" role="status">
          <Icone nome={offline ? 'wifi' : 'clock'} tamanho={15} traco={1.6} />
          <span>
            {offline ? 'Sem conexão' : 'Não foi possível atualizar'} · mostrando os dados das{' '}
            {horaCurta(new Date(ultimo.lidoEm).toISOString())} ({minutosAtras(ultimo.lidoEm, agora)}
            )
          </span>
          <button type="button" onClick={atualizar}>
            Atualizar
          </button>
        </div>
      )}

      <header className="vg-topo">
        <div className="vg-titulo">
          <div className="vg-saudacao">
            {saudacao()}, {primeiroNome(eu?.nomeExibicao ?? eu?.nome) || 'administrador'}
          </div>
          <h1>Visão geral</h1>
          <div className="vg-contexto">
            {capitalizar(diaLongo(hoje))} · {LOJA}
          </div>
        </div>
        <div className="vg-acoes">
          <button type="button" className="vg-botao vg-botao-leve" onClick={atualizar}>
            <Icone nome="retry" tamanho={14} traco={1.6} />
            {ultimo
              ? `Atualizado ${minutosAtras(ultimo.lidoEm, agora)}`
              : estado.fase === 'carregando'
                ? 'Carregando…'
                : 'Tentar de novo'}
          </button>
          <a className="vg-botao vg-some-celular" href="#/dashboard">
            <Icone nome="gauge" tamanho={15} traco={1.6} />
            Dashboard
          </a>
          {eu !== null && podeFazer(eu, 'painel.ver_completo') && (
            <a className="vg-botao vg-botao-escuro" href="#/tv">
              <Icone nome="panel" tamanho={15} traco={1.6} />
              Modo TV
            </a>
          )}
        </div>
      </header>

      <BarraDeFiltros f={f} hoje={hoje} equipe={equipe} />
      {d && <Fontes d={d} agora={agora} />}

      {!d && estado.fase !== 'carregando' ? (
        estado.fase === 'erro' && estado.falha.tipo === 'offline' ? (
          <div className="hm-cartao hm-cartao-estado">
            <EstadoVazio
              icone="wifi"
              titulo="Sem conexão"
              texto="Não foi possível carregar a visão geral. Nenhum dado foi carregado ainda neste aparelho."
            >
              <button type="button" className="es-botao" onClick={atualizar}>
                <Icone nome="cycle" tamanho={15} traco={1.6} />
                Tentar de novo
              </button>
            </EstadoVazio>
          </div>
        ) : estado.fase === 'erro' && estado.falha.tipo === 'sem-sessao' ? (
          <div className="hm-cartao hm-cartao-estado">
            <EstadoVazio
              icone="lock"
              titulo="Sua sessão terminou"
              texto="Entre de novo para ver a visão geral."
            >
              <button
                type="button"
                className="es-botao es-botao-primario"
                onClick={() => window.location.reload()}
              >
                Entrar de novo
              </button>
            </EstadoVazio>
          </div>
        ) : (
          <ErroDeArea oQue="a visão geral da operação" aoTentar={atualizar} />
        )
      ) : (
        <Corpo
          d={d}
          g={galeria.dados}
          carregandoG={galeria.carregando}
          f={f}
          quando={quando}
          hoje={hoje}
        />
      )}
    </section>
  );
}

/** De onde vêm os números e de quando: uma linha, com a cor do estado de cada fonte. */
function Fontes({ d, agora }: { d: PainelDoDia; agora: number }) {
  return (
    <div className="vg-fontes" aria-label="Frescor das fontes">
      {d.frescor.map((x) => (
        <span
          key={x.fonte}
          className={`vg-fonte vg-fonte-${x.estado === 'ok' ? 'ok' : x.estado === 'indisponivel' ? 'fora' : 'velho'}`}
          title={x.detalhe}
        >
          <i />
          {x.rotulo}
          <b>
            {x.ate
              ? x.fonte === 'relatorio_hos' || x.fonte === 'estoque'
                ? `lido ${minutosAtras(Date.parse(x.ate), agora)}`
                : `${diaCurto(new Date(x.ate).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }))} ${horaCurta(x.ate)}`
              : 'sem dado'}
          </b>
        </span>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Corpo
 * ------------------------------------------------------------------ */

function Corpo({
  d,
  g,
  carregandoG,
  f,
  quando,
  hoje,
}: {
  d: PainelDoDia | null;
  g: GaleriaDeGraficos | null;
  carregandoG: boolean;
  f: ReturnType<typeof lerFiltros>;
  quando: string;
  hoje: string;
}) {
  const umDia = f.periodo.de === f.periodo.ate;
  // hoje, loja inteira: o painel do dia compara ATÉ A MESMA HORA (justo); o resto vem da galeria
  // (o painel de uma API mais antiga, sem cupons/ticket, cai na galeria em vez de quebrar a tela)
  const doPainel =
    umDia &&
    !f.pessoa &&
    d !== null &&
    d.dia === f.periodo.de &&
    'cupons' in d.kpis &&
    'ticket' in d.kpis;
  const fat = g ? soma(g.diario.map((x) => x.faturamento)) : 0;
  const fatAnt = g ? soma(g.diarioAnterior.map((x) => x.faturamento)) : 0;
  const cup = g ? soma(g.diario.map((x) => x.cupons)) : 0;
  const cupAnt = g ? soma(g.diarioAnterior.map((x) => x.cupons)) : 0;
  const itn = g ? soma(g.diario.map((x) => x.itens)) : 0;
  const itnAnt = g ? soma(g.diarioAnterior.map((x) => x.itens)) : 0;
  const blocos = g ? agrupar(g.diario) : [];
  const trilha = umDia
    ? (g?.porHora ?? []).map((h) => h.faturamento)
    : blocos.map((b) => b.faturamento);
  const trilhaCupons = umDia
    ? (g?.porHora ?? []).map((h) => h.cupons)
    : blocos.map((b) => b.cupons);
  const trilhaTicket = umDia
    ? (g?.porHora ?? []).map((h) => (h.cupons ? h.faturamento / h.cupons : 0))
    : blocos.map((b) => b.ticket ?? 0);
  const contra = umDia ? `vs ${diaCurto(g?.anterior.de ?? f.periodo.de)}` : 'vs período anterior';
  const premio = g ? soma(g.programas.map((x) => x.premio)) : 0;
  const itensProg = g ? soma(g.programas.map((x) => x.itens)) : 0;
  const alvo = g?.meta.itensPorPessoa ?? 0;
  const metaPessoas = g?.meta.pessoas ?? [];
  const metaItens = soma(metaPessoas.map((p) => Math.min(alvo, p.itens)));
  const metaAlvo = alvo * metaPessoas.length;
  const bateram = metaPessoas.filter((p) => p.itens >= alvo).length;
  const semVenda = g !== null && cup === 0;

  const kpiDoPainel = (i: Indicador | undefined) =>
    i && i.disponivel && i.variacao
      ? { pct: i.variacao.pct, base: i.variacao.base, tom: tomDaVariacao(i) }
      : null;

  return (
    <>
      <div className="vg-kpis">
        <Kpi
          rotulo={`Faturamento · ${quando}`}
          icone="money"
          valor={
            doPainel && d.kpis.vendas.disponivel
              ? dinheiro(d.kpis.vendas.valor)
              : g
                ? fmt(fat, 'reais')
                : null
          }
          variacao={
            doPainel ? kpiDoPainel(d.kpis.vendas) : g ? pctVar(variacao(fat, fatAnt), contra) : null
          }
          sub={semVenda ? 'nenhuma venda do HOS no período' : null}
          trilha={trilha}
          link="#/vendas"
          destaque
        />
        <Kpi
          rotulo="Cupons"
          icone="users"
          valor={
            doPainel && d.kpis.cupons.disponivel
              ? inteiro(d.kpis.cupons.valor)
              : g
                ? fmt(cup, 'inteiro')
                : null
          }
          variacao={
            doPainel ? kpiDoPainel(d.kpis.cupons) : g ? pctVar(variacao(cup, cupAnt), contra) : null
          }
          sub={g && cup ? `${fmt(itn, 'inteiro')} itens vendidos` : null}
          trilha={trilhaCupons}
          link="#/vendas"
        />
        <Kpi
          rotulo="Ticket médio"
          icone="receipt"
          valor={
            doPainel && d.kpis.ticket.disponivel
              ? fmt(d.kpis.ticket.valor, 'reais')
              : g && cup
                ? fmt(fat / cup, 'reais')
                : g
                  ? '—'
                  : null
          }
          variacao={
            doPainel
              ? kpiDoPainel(d.kpis.ticket)
              : g && cup && cupAnt
                ? pctVar(variacao(fat / cup, fatAnt / cupAnt), contra)
                : null
          }
          sub={null}
          trilha={trilhaTicket}
          link="#/vendas"
        />
        <Kpi
          rotulo="Itens por cupom"
          icone="pill"
          valor={g && cup ? fmt(itn / cup, 'decimal') : g ? '—' : null}
          variacao={
            g && cup && cupAnt ? pctVar(variacao(itn / cup, itnAnt / cupAnt), contra) : null
          }
          sub={g && cup ? 'quantos itens cada cliente leva' : null}
          trilha={blocos.map((b) => (b.cupons ? b.itens / b.cupons : 0))}
          link="#/vendas"
        />
        <Kpi
          rotulo="Premiação gerada"
          icone="trophy"
          valor={g ? fmt(premio, 'reais') : null}
          variacao={null}
          sub={
            g
              ? itensProg
                ? `${fmt(itensProg, 'inteiro')} itens de programa lançados`
                : 'nenhum item de programa lançado'
              : null
          }
          link="#/premiacao"
        />
        <Kpi
          rotulo="Meta da semana"
          icone="gauge"
          valor={g ? (metaAlvo ? `${fmt((metaItens / metaAlvo) * 100, 'pct')}` : '—') : null}
          variacao={null}
          sub={
            g
              ? metaPessoas.length
                ? `${String(bateram)} de ${String(metaPessoas.length)} bateram os ${String(alvo)} itens`
                : 'ninguém lançou item nesta semana'
              : null
          }
          barra={metaAlvo ? (metaItens / metaAlvo) * 100 : null}
          link="#/ranking"
        />
      </div>

      <div className="vg-linha vg-linha-principal">
        <div className="vg-grafico">
          {!g ? (
            <div className="gx-cartao vg-osso" style={{ height: 392 }} aria-busy="true" />
          ) : umDia && doPainel && d.porHora.horas.length ? (
            <HoraAHora d={d} g={g} quando={quando} />
          ) : (
            <GraficoPrincipal g={g} />
          )}
          {carregandoG && g && <span className="vg-recarga">atualizando…</span>}
        </div>
        {d ? (
          <Atencao itens={d.atencao} premiacao={d.kpis.premiacao} />
        ) : (
          <div className="hm-cartao vg-osso" />
        )}
      </div>

      <div className="vg-linha vg-linha-3">
        <Ranking g={g} f={f} quando={quando} />
        <Programas g={g} quando={quando} />
        <MaisVendidos g={g} quando={quando} />
      </div>

      <div className="vg-linha vg-linha-principal">
        {d ? (
          <Operacao
            modulos={d.operacao}
            giro={fonteDo(d.frescor, 'giro')?.ate ?? null}
            hoje={hoje}
          />
        ) : (
          <div className="hm-cartao vg-osso" />
        )}
        {d ? <Atividade linhas={d.atividade} /> : <div className="hm-cartao vg-osso" />}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Hoje: hora a hora contra a média dos 4 mesmos dias da semana
 * ------------------------------------------------------------------ */

function HoraAHora({ d, g, quando }: { d: PainelDoDia; g: GaleriaDeGraficos; quando: string }) {
  const s = d.porHora;
  const horas = s.horas.filter((h) => !h.futura || h.base !== null);
  const fat = soma(s.horas.map((h) => h.atual));
  const pico = [...s.horas].filter((h) => !h.futura).sort((a, b) => b.atual - a.atual)[0];
  const k = d.kpis.vendas;
  const acumulado = s.horas.filter((h) => !h.futura);
  const fatBase = soma(acumulado.map((h) => h.base ?? 0));
  return (
    <CartaoDeGrafico
      id="vg-hora"
      titulo={`Faturamento hora a hora · ${quando}`}
      numero={fmt(fat, 'reais')}
      variacao={
        k.disponivel && k.variacao
          ? { pct: k.variacao.pct, bom: 'subir', base: k.variacao.base }
          : null
      }
      conclusao={
        pico ? (
          <>
            Horário mais forte até agora: <strong>{String(pico.hora)}h</strong> (
            {fmt(pico.atual, 'reais')}).{' '}
            {s.base ? (
              <>
                Nas mesmas horas, a {s.base} foi <strong>{fmt(fatBase, 'reais')}</strong>. A linha
                cinza é essa média.
              </>
            ) : (
              'Sem base das 4 semanas anteriores para comparar.'
            )}
          </>
        ) : (
          'Nenhuma venda do HOS hoje ainda.'
        )
      }
      modelos={MODELOS_TEMPO}
      padrao="agrupadas"
      desenhar={(m) =>
        serieNoTempo(m, {
          cats: horas.map((h) => `${String(h.hora).padStart(2, '0')}h`),
          atual: horas.map((h) => (h.futura ? null : h.atual)),
          anterior: horas.map((h) => h.base),
          nome: quando === 'hoje' ? 'Hoje' : quando,
          nomeAnterior: s.base ? capitalizar(s.base) : 'Média',
          formato: 'reais',
        })
      }
      fonte="Fonte: vendas do HOS (API, automática)"
      vazio={fat ? null : 'Nenhuma venda do HOS hoje ainda.'}
      altura={300}
      extra={
        g.porHora.length ? (
          <span className="vg-extra">
            {fmt(soma(g.porHora.map((h) => h.cupons)), 'inteiro')} cupons
          </span>
        ) : null
      }
    />
  );
}

/* ------------------------------------------------------------------ *
 * Ranking, programas e mais vendidos do período
 * ------------------------------------------------------------------ */

function Ranking({
  g,
  f,
  quando,
}: {
  g: GaleriaDeGraficos | null;
  f: ReturnType<typeof lerFiltros>;
  quando: string;
}) {
  const linhas = g?.porBalconista ?? [];
  const max = Math.max(1, ...linhas.map((l) => l.faturamento));
  const total = soma(linhas.map((l) => l.faturamento));
  return (
    <Bloco
      titulo="Quem mais vendeu"
      sub={`Faturamento por balconista · ${quando}${f.pessoa ? ' · filtrado' : ''}`}
      link="#/ranking"
      rotuloDoLink="Ranking"
    >
      {!g ? (
        <Carregando />
      ) : linhas.length === 0 ? (
        <p className="vg-vazio">Nenhuma venda do HOS no período.</p>
      ) : (
        <ol className="vg-ranking">
          {linhas.slice(0, 7).map((l, i) => {
            return (
              <li key={`${l.codigo ?? l.nome}`}>
                <span className={`vg-pos vg-pos-${String(Math.min(4, i + 1))}`}>{i + 1}</span>
                <span className="vg-avatar">{iniciais(l.nome)}</span>
                <span className="vg-rank-nome">
                  <strong title={l.nome}>{nomeCurto(l.nome)}</strong>
                  <span>
                    {fmt(l.cupons, 'inteiro')} cupons · ticket {fmt(l.ticket, 'reais')} ·{' '}
                    {fmt(l.itensPorCupom, 'decimal')} itens/cupom
                  </span>
                  <span className="vg-rank-barra">
                    <i style={{ width: `${String((l.faturamento / max) * 100)}%` }} />
                  </span>
                </span>
                <span className="vg-rank-valor">
                  <strong>{fmt(l.faturamento, 'reais')}</strong>
                  <span>{total ? fmt((l.faturamento / total) * 100, 'pct') : ''}</span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </Bloco>
  );
}

const PROGRAMAS = [
  { id: 'vitamina' as const, nome: 'Vitamina', cor: '#1a7f55', link: '#/vitamina' },
  { id: 'pre_vencido' as const, nome: 'Pré-vencido', cor: '#cb1b2b', link: '#/pre-vencidos' },
  { id: 'paradao' as const, nome: 'Paradão', cor: '#b7791f', link: '#/paradao' },
];

function Programas({ g, quando }: { g: GaleriaDeGraficos | null; quando: string }) {
  const linhas = PROGRAMAS.map((p) => {
    const xs = (g?.programas ?? []).filter((x) => x.programa === p.id);
    return { ...p, itens: soma(xs.map((x) => x.itens)), premio: soma(xs.map((x) => x.premio)) };
  });
  const max = Math.max(1, ...linhas.map((l) => l.itens));
  const total = soma(linhas.map((l) => l.premio));
  return (
    <Bloco
      titulo="Programas"
      sub={`Itens lançados e premiação · ${quando}`}
      link="#/premiacao"
      rotuloDoLink="Premiação"
    >
      {!g ? (
        <Carregando />
      ) : (
        <>
          <div className="vg-prog-total">
            <strong>{fmt(total, 'reais')}</strong>
            <span>em premiação gerada no período</span>
          </div>
          <ul className="vg-programas">
            {linhas.map((l) => (
              <li key={l.id}>
                <a href={l.link}>
                  <span className="vg-prog-cor" style={{ background: l.cor }} />
                  <span className="vg-prog-nome">{l.nome}</span>
                  <span className="vg-prog-num">
                    <strong>{fmt(l.itens, 'inteiro')}</strong> itens
                  </span>
                  <span className="vg-prog-premio">{fmt(l.premio, 'reais')}</span>
                  <span className="vg-prog-barra">
                    <i style={{ width: `${String((l.itens / max) * 100)}%`, background: l.cor }} />
                  </span>
                </a>
              </li>
            ))}
          </ul>
          {total === 0 && (
            <p className="vg-nota">
              Nenhum item de programa lançado no InovaPay 2.0 neste período — os lançamentos ainda
              acontecem no 1.0 até a troca.
            </p>
          )}
        </>
      )}
    </Bloco>
  );
}

export function MaisVendidos({ g, quando }: { g: GaleriaDeGraficos | null; quando: string }) {
  const linhas = (g?.topProdutos ?? []).slice(0, 7);
  const max = Math.max(1, ...linhas.map((l) => l.faturamento));
  return (
    <Bloco
      titulo="Mais vendidos"
      sub={`Produtos por faturamento · ${quando}`}
      link="#/vendas"
      rotuloDoLink="Vendas"
    >
      {!g ? (
        <Carregando />
      ) : linhas.length === 0 ? (
        <p className="vg-vazio">Nenhuma venda do HOS no período.</p>
      ) : (
        <ol className="vg-produtos">
          {linhas.map((l, i) => (
            <li key={`${l.codigo ?? l.nome}`}>
              <span className="vg-prod-pos">{i + 1}</span>
              <span className="vg-prod-nome">
                <strong title={l.nome}>{l.nome}</strong>
                <span className="vg-rank-barra">
                  <i style={{ width: `${String((l.faturamento / max) * 100)}%` }} />
                </span>
              </span>
              <span className="vg-rank-valor">
                <strong>{fmt(l.faturamento, 'reais')}</strong>
                <span>{fmt(l.quantidade, 'inteiro')} un.</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </Bloco>
  );
}

/* ------------------------------------------------------------------ *
 * Precisa de atenção
 * ------------------------------------------------------------------ */

function iconeDaAtencao(a: ItemDeAtencao): NomeDoIcone {
  if (a.tipo === 'pedidos') return 'users';
  if (a.tipo === 'eans') return 'scan';
  if (a.tipo === 'bloqueios') return 'lock';
  if (a.tipo.startsWith('giro') || a.tipo.includes('responsavel') || a.tipo.includes('bloco'))
    return 'clock';
  if (a.link.includes('giro')) return 'clock';
  return a.severidade === 'info' ? 'bell' : 'warn';
}

export function Atencao({
  itens,
  premiacao,
}: {
  itens: readonly ItemDeAtencao[];
  premiacao: Indicador;
}) {
  return (
    <div className="hm-cartao hm-atencao vg-atencao">
      <div className="hm-cartao-topo">
        <h2>Precisa de atenção</h2>
        {itens.length > 0 && <span className="hm-bola">{itens.length}</span>}
      </div>
      <div className="hm-atencao-lista">
        {itens.length === 0 ? (
          <div className="hm-vazio hm-vazio-ok">
            <Icone nome="check" tamanho={17} traco={1.8} />
            <strong>Nada pede decisão agora</strong>
            <span>Divergências, Giro, validades e cadastros sem pendência.</span>
          </div>
        ) : (
          itens.map((a) => (
            <a
              key={`${a.tipo}-${a.titulo}`}
              className="hm-atencao-item"
              href={a.link}
              title={a.acao}
            >
              <span className={`hm-sev hm-sev-${a.severidade}`}>
                <Icone nome={iconeDaAtencao(a)} tamanho={17} traco={1.6} />
              </span>
              <span className="hm-atencao-texto">
                <strong>{a.titulo}</strong>
                {a.detalhe && <span>{a.detalhe}</span>}
              </span>
              <Icone nome="chev-r" tamanho={15} traco={1.6} />
            </a>
          ))
        )}
      </div>
      {premiacao.disponivel && (
        <a className="hm-cheque" href={premiacao.link}>
          <Icone nome="money" tamanho={16} traco={1.6} />
          <span>
            <b>Cheque da semana</b> · {dinheiro(premiacao.valor)}
            {premiacao.detalhe ? ` · ${premiacao.detalhe}` : ''}
          </span>
          <strong>Abrir</strong>
        </a>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Operação de hoje
 * ------------------------------------------------------------------ */

const ICONE_DO_MODULO: Record<ModuloDaOperacao['modulo'], NomeDoIcone> = {
  giro: 'cycle',
  vendas: 'receipt',
  premiacao: 'money',
  pre_vencidos: 'clock',
  paradao: 'box',
};

export function Operacao({
  modulos,
  giro,
  hoje,
}: {
  modulos: readonly ModuloDaOperacao[];
  giro: string | null;
  hoje: string;
}) {
  return (
    <div className="hm-cartao hm-operacao vg-operacao">
      <div className="hm-cartao-topo">
        <h2>Operação de hoje · {diaCurto(hoje)}</h2>
        {giro && (
          <span className="hm-cartao-dir">último registro do Giro às {horaCurta(giro)}</span>
        )}
      </div>
      {modulos.length === 0 ? (
        <div className="hm-vazio">
          <Icone nome="grid" tamanho={17} traco={1.5} />
          <strong>Nenhum módulo com operação hoje</strong>
        </div>
      ) : (
        <ul className="vg-op-lista">
          {modulos.map((m) => (
            <li key={m.modulo}>
              <a className="vg-op" href={m.link}>
                <span className="vg-op-icone">
                  <Icone nome={ICONE_DO_MODULO[m.modulo]} tamanho={17} traco={1.6} />
                </span>
                <span className="vg-op-nome">
                  <strong>{m.titulo}</strong>
                  <span>{comMilhar(m.subtitulo)}</span>
                </span>
                <span className="vg-op-resumo" title={m.resumo}>
                  {m.progresso !== null && (
                    <span className="vg-op-barra">
                      <i style={{ width: `${String(Math.max(0, Math.min(100, m.progresso)))}%` }} />
                    </span>
                  )}
                  {comMilhar(m.resumo)}
                </span>
                <span className="vg-op-selos">
                  {m.selos.map((x) => (
                    <span key={x.texto} className={`hm-selo hm-selo-${x.tom}`}>
                      {x.texto}
                    </span>
                  ))}
                </span>
                <span className="vg-op-acao">
                  {m.acao}
                  <Icone nome="chev-r" tamanho={13} traco={1.8} />
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Atividade recente (a mesma ação repetida vira uma linha)
 * ------------------------------------------------------------------ */

function Atividade({ linhas }: { linhas: PainelDoDia['atividade'] }) {
  const hoje = hojeBrasilia();
  const quandoFoi = (iso: string) => {
    const dia = new Date(iso).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
    return dia === hoje ? horaCurta(iso) : `${diaCurto(dia)} ${horaCurta(iso)}`;
  };
  return (
    <div className="hm-cartao vg-atividade">
      <div className="hm-cartao-topo">
        <h2>Atividade recente</h2>
        <a href="#/auditoria" className="vg-bloco-link">
          Auditoria
          <Icone nome="chev-r" tamanho={13} traco={1.8} />
        </a>
      </div>
      {linhas.length === 0 ? (
        <div className="hm-vazio">
          <Icone nome="clock" tamanho={17} traco={1.5} />
          <strong>Nenhuma atividade registrada ainda</strong>
          <span>Vendas, Giro, auditoria e aprovações aparecem aqui quando acontecem.</span>
        </div>
      ) : (
        <ul className="vg-atividade-lista">
          {linhas.slice(0, 8).map((a, k) => (
            <li key={`${a.em}-${String(k)}`}>
              <time dateTime={a.em}>{quandoFoi(a.em)}</time>
              <span className="vg-ativ-texto">
                {a.quem ? <b title={a.quem}>{primeiroNome(a.quem)} </b> : <b>Sistema </b>}
                {a.texto}
                {a.vezes > 1 && (
                  <span
                    className="vg-ativ-vezes"
                    title={`de ${quandoFoi(a.desde)} a ${quandoFoi(a.em)}`}
                  >
                    {String(a.vezes)} vezes desde {quandoFoi(a.desde)}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
